import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { BaseDialog } from 'src/library/components/BaseDialog';
import '../../library/fields/d-view-info';
import '../../library/elements/d-wrap';
import '../../library/elements/d-action';
import '../../library/elements/d-label';
import '../../library/fields/d-view-text';
import '../../library/fields/d-view-html';
import { isEmptyHtmlContent, numberWord } from 'src/utilities/text';
import type { SelectSuggestionOrEntityDialogInput } from 'src/content/risk-assessments/select-suggestion-or-entity-dialog-input';
import type { SelectSuggestionOrEntityDialogResult } from 'src/content/risk-assessments/select-suggestion-or-entity-dialog-result';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import type { TaskSummary } from 'src/content/risk-assessments/task-summary';
import { classMap } from 'lit/directives/class-map.js';

@customElement('select-suggestion-or-entity-dialog')
export class SelectSuggestionOrEntityDialog extends BaseDialog<
  SelectSuggestionOrEntityDialogInput,
  SelectSuggestionOrEntityDialogResult
> {
  static readonly styles = [
    ...BaseDialog.styles,
    css`
      .main-title {
        font-size: 22px;
        font-weight: 500;
      }
      .post-header {
        padding: 0 20px 12px 20px;
      }
      @container dialog (width > 700px) {
        .post-header {
          padding: 0 30px 12px 30px;
        }
      }
      .item {
        border-top: 1px solid var(--borderColor);
        padding: 16px 0;
      }
      .item-header {
        margin-bottom: 8px;
      }
      .name {
        font-size: 18px;
        font-weight: 500;
        padding: 4px 0 4px 32px;
      }
      .task .name {
        background: url(/images/tasks-color.svg) -2px 0 no-repeat;
        background-size: 30px auto;
      }
      .event .name {
        background: url(/images/event-occurrences-color.svg) -2px 0 no-repeat;
        background-size: 30px auto;
      }
      .suggestion .name {
        background: url(/images/suggestion-gray.svg) -10px -9px no-repeat;
        background-size: 46px auto;
      }
      .item-content {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      d-view-html {
        margin-top: 8px;
        border: 1px solid var(--borderColor);
        border-radius: 12px;
        padding: 2px 20px 14px 20px;
      }
    `,
  ];
  @property({ type: Number, attribute: 'width' })
  width = 720;
  @property({ type: String })
  riskAssessmentName = '';
  @property({ type: String })
  entityType: 'eventOccurrences' | 'tasks' = 'tasks';
  @property({ type: Array })
  entities: TaskSummary[] = [];
  @property({ type: Object })
  suggestion!: TaskSummary;
  @property({ type: String })
  selectedTaskUuid: string | undefined = undefined;

  private get entityTypeName() {
    return this.entityType === 'eventOccurrences'
      ? t('riskAssessments.selectSuggestionOrEntityDialog.event')
      : t('riskAssessments.selectSuggestionOrEntityDialog.task');
  }

  private get selectEntityButtonText() {
    return this.entityType === 'eventOccurrences'
      ? t('riskAssessments.selectSuggestionOrEntityDialog.selectEvent')
      : t('riskAssessments.selectSuggestionOrEntityDialog.selectTask');
  }

  private get selectSuggestionButtonText() {
    return this.entityType === 'eventOccurrences'
      ? t('riskAssessments.selectSuggestionOrEntityDialog.newEvent')
      : t('riskAssessments.selectSuggestionOrEntityDialog.newTask');
  }

  protected get calculatedTitle(): string {
    const entityTypeName = this.entityTypeName;
    return t('riskAssessments.selectSuggestionOrEntityDialog.title', { entityTypeName });
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [{ name: t('common:cancel'), action: 'close' }];
  }

  private onSelectOption(uuid: string | undefined = undefined) {
    this.selectedTaskUuid = uuid;
    return this.onDispatchAction(false, 'done');
  }

  protected fetchResult(detail: string | undefined): SelectSuggestionOrEntityDialogResult {
    if (detail === 'done') {
      return {
        action: 'done',
        uuid: this.selectedTaskUuid,
      };
    }
    return {
      action: 'cancel',
    };
  }

  private get infoText() {
    const numberTerm = this.entities.length === 1 ? 'en' : numberWord(this.entities.length);
    let entityTypeNamePlural = t('riskAssessments.selectSuggestionOrEntityDialog.tasks');
    let thisEntity = t('riskAssessments.selectSuggestionOrEntityDialog.thisTask');
    let oneOfTheseEntities = t('riskAssessments.selectSuggestionOrEntityDialog.oneOfTheseTasks');
    if (this.entityType === 'eventOccurrences') {
      entityTypeNamePlural = t('riskAssessments.selectSuggestionOrEntityDialog.events');
      thisEntity = t('riskAssessments.selectSuggestionOrEntityDialog.thisEvent');
      oneOfTheseEntities = t('riskAssessments.selectSuggestionOrEntityDialog.oneOfTheseEvents');
    }
    const entityTerm = this.entities.length === 1 ? this.entityTypeName : entityTypeNamePlural;
    const countedEntities = numberTerm + ' ' + entityTerm;
    const thisOrOneOf = this.entities.length === 1 ? thisEntity : oneOfTheseEntities;
    const riskAssessmentName = this.riskAssessmentName;
    const entityTypeName = this.entityTypeName;
    return t('riskAssessments.selectSuggestionOrEntityDialog.infoText', {
      countedEntities,
      thisOrOneOf,
      riskAssessmentName,
      entityTypeName,
    });
  }

  protected initializeDialog(input: SelectSuggestionOrEntityDialogInput) {
    this.riskAssessmentName = input.riskAssessmentName;
    this.entityType = input.entityType;
    this.entities = input.entities;
    this.suggestion = input.suggestion;
  }

  private itemClass(type: 'entity' | 'suggestion') {
    return {
      item: true,
      suggestion: type === 'suggestion',
      task: this.entityType === 'tasks',
      event: this.entityType === 'eventOccurrences',
    };
  }

  protected renderPostHeader() {
    return html`
      <div class="post-header"><d-view-info .content=${this.infoText} class="fullWidth"></d-view-info></div>
    `;
  }

  protected renderBody() {
    return html`
      ${this.entities.map((entity) => {
        return html`
          <div class="${classMap(this.itemClass('entity'))}">
            <d-wrap tight split-reverse baseline class="item-header">
              <d-action button @click=${() => this.onSelectOption(entity.uuid)}
                >${this.selectEntityButtonText}</d-action
              >
              <div class="name">${entity.name}</div>
            </d-wrap>
            <div class="item-content">
              <d-view-text
                inline-label
                small-label
                hide-tooltip
                .label=${t('riskAssessments.selectSuggestionOrEntityDialog.measureInRiskAssessment')}
                .value=${entity.relatedRiskAssessments
                  ? entity.relatedRiskAssessments
                  : t('riskAssessments.selectSuggestionOrEntityDialog.none')}
              ></d-view-text>
              <d-view-text
                inline-label
                small-label
                hide-tooltip
                .field=${this.entityType === 'tasks' ? 'tasks_function' : 'events_assignee'}
                .value=${entity.assignedTo}
              ></d-view-text>
              ${this.entityType === 'eventOccurrences'
                ? html`
                    <d-view-text
                      inline-label
                      small-label
                      hide-tooltip
                      .label=${t('riskAssessments.selectSuggestionOrEntityDialog.when')}
                      .value=${entity.time ?? ''}
                    ></d-view-text>
                  `
                : nothing}
              ${this.entityType === 'tasks'
                ? html`
                    <d-view-text
                      inline-label
                      small-label
                      hide-tooltip
                      field="tasks_responsible"
                      .value=${entity.responsibleFunction ?? ''}
                    ></d-view-text>
                  `
                : nothing}
              ${isEmptyHtmlContent(entity.procedure)
                ? html`
                    <d-view-text
                      inline-label
                      small-label
                      hide-tooltip
                      system-content
                      field="tasks_procedures"
                      .value=${t('riskAssessments.selectSuggestionOrEntityDialog.none')}
                    ></d-view-text>
                  `
                : html`
                    <div>
                      <d-label small hide-tooltip system-content field="tasks_procedures"> </d-label>
                      <d-view-html wrap-label hide-tooltip .value=${entity.procedure}></d-view-html>
                    </div>
                  `}
            </div>
          </div>
        `;
      })}
      <div class="${classMap(this.itemClass('suggestion'))}">
        <d-wrap tight split-reverse baseline class="item-header">
          <d-action button green @click=${() => this.onSelectOption()}>${this.selectSuggestionButtonText}</d-action>
          <div class="name">${this.suggestion.name}</div>
        </d-wrap>
        <div class="item-content">
          <d-view-text
            inline-label
            small-label
            hide-tooltip
            .field=${this.entityType === 'tasks' ? 'tasks_function' : ''}
            .label=${this.entityType === 'eventOccurrences'
              ? t('riskAssessments.selectSuggestionOrEntityDialog.toBeAssignedTo')
              : ''}
            .value=${this.suggestion.assignedTo}
          ></d-view-text>
          ${this.entityType === 'tasks'
            ? html`
                <d-view-text
                  inline-label
                  small-label
                  hide-tooltip
                  field="tasks_responsible"
                  .value=${this.suggestion.responsibleFunction ?? ''}
                ></d-view-text>
              `
            : nothing}
          ${isEmptyHtmlContent(this.suggestion.procedure)
            ? html`
                <d-view-text
                  inline-label
                  small-label
                  hide-tooltip
                  field="tasks_procedures"
                  .value=${t('riskAssessments.selectSuggestionOrEntityDialog.none')}
                ></d-view-text>
              `
            : html`
                <div>
                  <d-label small hide-tooltip system-content field="tasks_procedures"> </d-label>
                  <d-view-html wrap-label hide-tooltip .value=${this.suggestion.procedure}></d-view-html>
                </div>
              `}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'select-suggestion-or-entity-dialog': SelectSuggestionOrEntityDialog;
  }
}
